/* @flow */
import ms from 'ms';
import isNil from 'lodash.isnil';
import { Record } from 'immutable';
import { Map, List } from 'immutable';
import { combineReducers } from 'redux-immutable';

import type { Action } from 'trustar/utils/redux-types.js';
import { pickImmutable } from 'trustar/utils';

import {
  FETCH_SERVER_SESSION_TIMEOUT_SUCCESS,
  SET_TIMEOUT_WARNING,
  SET_OPTIONS,
  SET_ACTIVITY,
  INITIALIZE_SUCCESS,
  CONFIRMATION,
  SET_LAST_ROUTE,
  LOGIN,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT,
  LOGOUT_SUCCESS,
  LOGOUT_FAILURE,
  USER_CREDENTIALS_UPDATE,
  USER_CREDENTIALS_CLEAR,
  ACCEPT_TERMS_SUCCESS,
  ACCEPT_TERMS_FAILURE,
  INITIALIZE,
  LOGIN_REDIRECT,
  CHANGE_PASSWORD_CREDENTIALS_UPDATE,
  CHANGE_PASSWORD_VALIDATE,
  CHANGE_PASSWORD_FAILURE_BAD_LINK,
  CHANGE_PASSWORD_FAILURE_EXPIRED_TOKEN,
  CHANGE_PASSWORD_FAILURE,
  FORGOT_PASSWORD_SUCCESS,
  LOGIN_CLEAR_STATUS_MESSAGE_SUCCESS,
  DECLINE_TERMS,
  MFA_VERIFY_RESPONSE_FAILURE,
  FORCE_LOGIN,
  DID_LOGIN_VIA_SSO,
} from './action-types.js';

import {
  IS_LOGGED_IN_LOCALSTORAGE_KEY,
  STORED_USERNAME_LOCALSTORAGE_KEY,
} from './constants';

import type {
  LoginObjectType,
  LoginStatusMessageType,
} from './types.js';
import {CLIPBOARD_COPY, CONFIRM_ALIVE, CONFIRM_ALIVE_SUCCESS} from './action-types';
import {getCsrfToken} from '../../../../web_modules/trustar/utils';

export const timeoutWarning = (state: boolean = false, action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case SET_TIMEOUT_WARNING:
    return payload;
  default:
    return state;
  }
};

export const serverTimeout = (state: number = ms('16m'), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_SERVER_SESSION_TIMEOUT_SUCCESS:
    return ms(String(payload) + 's'); // Payload is a number
  default:
    return state;
  }
};

const Options = Map({
  theme: 'light',
});

export const options = (state: typeof Options = Options, action: Action<*>) => {
  const { type, payload } = action;
  switch (type) {
  case SET_OPTIONS:
    return state.merge(payload);
  default:
    return state;
  }
};

const Activity = Record({
  visitedConstellation: false,
  visitedDashboard: false,
  visitedExplore: false,
  visitedReports: false,
  visitedEntities: false,
});

export const activity = (state: typeof Activity = Activity(), action: Action<*>) => {
  const { type, payload } = action;
  switch (type) {
  case SET_ACTIVITY:
    return state.merge(payload);
  default:
    return state;
  }
};

/* Records route history of current app session
 *
 * NOTE: This records whatever route history exists during a user session.
 * We don't user this beyond retrieving the 'last' route for now... could refactor
 * this later to limit to last 10 routes, although it's unlikely a user is going
 * to visit 100s of routes in a single session. But this does explicitly consume
 * memory we're not explicitly using at the moment.
 */
export const routeHistory = (state: List<string> = List(), action: Action<*>) => {
  const { type, payload } = action;
  switch (type) {
  case SET_LAST_ROUTE:
    return state.push(payload);
  default:
    return state;
  }
};

/* State to track confirmation modal */
const Confirmation = Record({
  visible: true,
  message: '',
  accept: null,
});

export const confirmation = (state: typeof Options = Confirmation({ visible: false }), action: Action<*>) => {
  const { type, payload } = action;
  switch (type) {
  case CONFIRMATION:
    return Confirmation(payload);
  default:
    return state;
  }
};

/*
  This can be utilized as a global loading state for the app. Currently it only
  responds to loading data from Redux-Persist.
*/
export const loading = (state: boolean = true, action: Action<*>) => {
  switch(action.type) {
  case INITIALIZE_SUCCESS:
    return false;
  default:
    return state;
  }
};

export const serialize = (state: Map<any, any>) =>
  pickImmutable(['options'], state);

const initialLoggedInState = window.localStorage.getItem(IS_LOGGED_IN_LOCALSTORAGE_KEY) !== null;

export const isLoggedIn = (state: boolean = initialLoggedInState, action: Action<*>) => {
  switch(action.type) {
  case DID_LOGIN_VIA_SSO:
  case LOGIN_SUCCESS:
  case CONFIRM_ALIVE_SUCCESS:
    return true;
  case LOGOUT_SUCCESS:
  case LOGOUT_FAILURE:
  case LOGIN_FAILURE:
    return false;
  default:
    return state;
  }
};

export const userCredentials = (state: LoginObjectType = { username: '', password: ''}, action: Action<*>) => {
  switch(action.type) {
  case USER_CREDENTIALS_UPDATE:
    return action.payload;
  case USER_CREDENTIALS_CLEAR:
  case LOGIN_SUCCESS:
    return { username: '', password: ''};
  default:
    return state;
  }
};

const disabledLoginStatusMessage = { message: '', icon: '', level: 'disabled'};

export const statusMessage = (
  state: LoginStatusMessageType = disabledLoginStatusMessage,
  action: Action<*>,
) => {
  switch(action.type) {
    case LOGIN_CLEAR_STATUS_MESSAGE_SUCCESS:
    case LOGIN_SUCCESS:
      return disabledLoginStatusMessage;
    case LOGIN_FAILURE:
      const { status, accountLocked } = action.payload;

      // If the request timed out.
      if (status === 0) {
        return {
          message: 'An error occurred. Please try again momentarily or reach out to us at support@trustar.co if this issue persists.',
          level: 'warning',
        };
      }

      if (!isNil(accountLocked)) {
        if (accountLocked) {
          return {
            message: `
              <p>
                You have tried to enter your credentials too many times. Try again later
                or click <a class="diff-link" href="/auth/forgot-password">here</a> to reset your password.
              </p>
            `,
            level: 'danger',
          };
        }
      }

      return {
        message: `
          <p>
            Wrong username and/or password.
            Try again or click <a class="diff-link" href="/auth/forgot-password">here</a> to reset your password.
          </p>
        `,
        level: 'warning',
      };
    case MFA_VERIFY_RESPONSE_FAILURE:
      return {
        message: 'Your Multi-Factor Authentication attempt failed. This may be caused by a timeout or invalid credentials. Try again or contact support@trustar.co for further information.',
        level: 'warning',
      };
    case DECLINE_TERMS:
      return {
        message: 'You declined the Terms of Use (TOU). Please send an email to support@trustar.co for further information.',
        level: 'warning',
      };
    case FORGOT_PASSWORD_SUCCESS:
      return {
        message: 'Password reset instructions have been sent to the email you provided.',
        level: 'success',
      };
    case CHANGE_PASSWORD_FAILURE_BAD_LINK:
      return {
        message: 'Your activation link has expired. We sent a new one to your email inbox, please follow the new link activate your account.',
        level: 'warning',
      };
    case CHANGE_PASSWORD_FAILURE_EXPIRED_TOKEN:
      return {
        message: action.payload.response.message,
        level: 'warning',
      };
    case CHANGE_PASSWORD_FAILURE:
      return {
        message: 'An error occurred. Please reach out to us at support@trustar.co if this issue persists.',
        level: 'warning',
      };
    default:
      return state;
  }
};

export const loadingLogin = (state: boolean = false, action: Action<*>) => {
  switch(action.type) {
  case LOGIN:
  case CONFIRM_ALIVE:
    return true;
  case LOGIN_SUCCESS:
  case LOGIN_FAILURE:
  case LOGIN_REDIRECT:
  case LOGOUT_SUCCESS:
  case LOGOUT_FAILURE:
  case CONFIRM_ALIVE_SUCCESS:
    return false;
  default:
    return state;
  }
};

export const mfa = (state: Object = {}, action: Action<*>) => {
  switch(action.type) {
  case LOGIN_SUCCESS:
    return {};
  case LOGIN_REDIRECT:
    if (action.payload.state === 'REQUIRES_MFA') {
      return action.payload.mfa;
    }
    return state;
  default:
    return state;
  }
};

export const didAcceptTerms = (state: boolean = false, action: Action<*>) => {
  switch(action.type) {
  case ACCEPT_TERMS_SUCCESS:
    return true;
  case ACCEPT_TERMS_FAILURE:
    return false;
  default:
    return state;
  }
};

export const createPasswordAttempt = (state: string = '', action: Action<*>) => {
  switch(action.type) {
  case CHANGE_PASSWORD_CREDENTIALS_UPDATE:
    return action.payload;
  default:
    return state;
  }
};

const emptyPasswordValidation = {
  all: false,
  min: false,
  max: false,
  uppercase: false,
  lowercase: false,
  digits: false,
  symbols: false,
};

export const passwordValidation = (state: Object = emptyPasswordValidation, action: Action<*>) => {
  switch(action.type) {
  case CHANGE_PASSWORD_VALIDATE:
    return action.payload;
  default:
    return state;
  }
};

export const intendedPath = (state: string = '/', action: Action<*>) => {
  switch(action.type) {
  case FORCE_LOGIN:
    return action.payload;
  case LOGIN_SUCCESS:
    return '/';
  default:
    return state;
  }
};

const clipboardCopied = (state: boolean = false, action: Action<*>) => {
  const { type, payload } = action;

  switch(type) {
  case CLIPBOARD_COPY:
    return payload ? true : false;
  default:
    return state;
  }
};

export default combineReducers({
  timeoutWarning,
  serverTimeout,
  options,
  activity,
  routeHistory,
  confirmation,
  loading,
  isLoggedIn,
  userCredentials,
  statusMessage,
  loadingLogin,
  mfa,
  didAcceptTerms,
  createPasswordAttempt,
  passwordValidation,
  intendedPath,
  clipboardCopied,
});



// WEBPACK FOOTER //
// ./src/app/modules/app/stores.js